import React, { useState, useEffect, useCallback } from "react";
import ProjectModuleApi from "../../api/ProjectModule";
import Auth from "../../utils/AuthUtils";
import { can } from "../../utils/permissionUtils";
import { Navigate } from "react-router-dom";
import { showError, showSuccess } from "../../utils/toastUtils";

import AddDetails from "./AddDetails";
import EditDetails from "./EditDetails";

function List() {
  const [moduleData, setModuleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const user = Auth.getUserDetails();
  const permissions = user?.permissions;

  const fetchModuleData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ProjectModuleApi.list();
      if (res.code === 200) {
        setModuleData(res.data);
      }
    } catch (err) {
      showError("Failed to fetch project modules");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (can(permissions, "read", "projectModules")) {
      fetchModuleData();
    } else {
      setLoading(false);
    }
  }, []);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = moduleData.map((item) => item._id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await ProjectModuleApi.toggleStatus({ id });
      if (res.code === 200) {
        showSuccess(res.message);
        fetchModuleData(); 
      }
    } catch (err) {
      showError("Failed to change status");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await ProjectModuleApi.remove({ id: deleteId });
      if (res.code === 200) {
        showSuccess(res.message);
        fetchModuleData();
        setDeleteId(null);
        setSelectedIds([]);
        
        const modalElement = document.getElementById("deleteModal");
        if (window.bootstrap) {
          const modalInstance = window.bootstrap.Modal.getInstance(modalElement) || new window.bootstrap.Modal(modalElement);
          modalInstance.hide();
        }
      }
    } catch {
      showError("Delete failed");
    }
  };

  if (!can(permissions, "read", "projectModules")) {
    return <Navigate to="/403" replace />;
  }

  return (
    <div className="content pb-0">
      <div className="d-flex align-items-center justify-content-between gap-2 mb-4 flex-wrap">
        <div>
          <h4 className="mb-1 fw-bold">
            Project Modules
            <span className="badge badge-soft-primary ms-2">{moduleData.length} Total</span>
          </h4>
        </div>

        {can(permissions, "write", "projectModules") && (
          <button className="btn btn-primary" data-bs-toggle="offcanvas" data-bs-target="#offcanvas_add">
            <i className="ti ti-square-rounded-plus-filled me-1" /> Add Module
          </button>
        )}
      </div>

      <div className="card shadow-sm border-0 rounded-0">
        <div className="card-body p-0">
          <div className="table-responsive custom-table">
            <table className="table table-hover table-nowrap align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "50px" }}>
                    <input 
                      type="checkbox" 
                      className="form-check-input" 
                      onChange={handleSelectAll}
                      checked={moduleData.length > 0 && selectedIds.length === moduleData.length}
                    />
                  </th>
                  <th>Module Name</th>
                  <th>Estimated Hours</th>
                  <th>Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center p-5">Loading...</td></tr>
                ) : moduleData.length > 0 ? (
                  moduleData.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <input 
                          type="checkbox" 
                          className="form-check-input" 
                          checked={selectedIds.includes(item._id)}
                          onChange={() => handleSelectRow(item._id)}
                        />
                      </td>
                      <td className="fw-medium text-dark">{item.module_name}</td>
                      <td>
                        <span className="badge badge-soft-info p-2">
                          <i className="ti ti-clock me-1" />{item.estimated_hours} Hrs
                        </span>
                      </td>
                      <td>
                        <span 
                          className={`badge ${item.status === "inactive" ? "bg-danger" : "bg-success"} cursor-pointer`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleToggleStatus(item._id)}
                          title="Click to toggle status"
                        >
                          {item.status || "active"}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          {can(permissions, "write", "projectModules") && (
                            <button className="btn btn-sm btn-soft-primary" data-bs-toggle="offcanvas" data-bs-target="#offcanvas_edit" onClick={() => setEditData(item)}>
                              <i className="ti ti-edit" />
                            </button>
                          )}
                          {can(permissions, "delete", "projectModules") && (
                            <button className="btn btn-sm btn-soft-danger" data-bs-toggle="modal" data-bs-target="#deleteModal" onClick={() => setDeleteId(item._id)}>
                              <i className="ti ti-trash" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={5} className="text-center p-5 text-muted">No modules found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="offcanvas offcanvas-end" tabIndex={-1} id="offcanvas_add">
        <AddDetails fetchModuleData={fetchModuleData} />
      </div>
      <div className="offcanvas offcanvas-end" tabIndex={-1} id="offcanvas_edit">
        <EditDetails data={editData} fetchModuleData={fetchModuleData} />
      </div>

      <div className="modal fade" id="deleteModal" tabIndex={-1}>
        <div className="modal-dialog modal-sm modal-dialog-centered">
          <div className="modal-content border-0 shadow">
            <div className="modal-body text-center p-4">
              <h5 className="mb-3 fw-bold">Are you sure?</h5>
              <p className="text-muted small">This module will be permanently removed.</p>
              <div className="d-flex gap-2">
                <button className="btn btn-light w-100" data-bs-dismiss="modal">Cancel</button>
                <button className="btn btn-danger w-100" onClick={handleDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default List;